export type ImageSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_SUFFIX_RE = /\/(sm|md|lg|xl)$/i;
const IWG_CLOUDINARY_RE = /^(https?:\/\/assets\.iwgplc\.com\/image\/upload\/)(.+)$/i;
// A Cloudinary transformation segment such as "w_960,c_limit,q_auto,f_auto/".
const IWG_TRANSFORMED_RE = /^[a-z]{1,3}_[^/]*\//i;

// Close to the widths of the S3 /sm /md /lg variants, so IWG photos look the same.
const IWG_WIDTHS: Record<ImageSize, number> = { sm: 480, md: 960, lg: 1600, xl: 2000 };

export const stripSizeSuffix = (src: string) => src.replace(SIZE_SUFFIX_RE, '');

/**
 * Some partner photo URLs from the API are plain http:// (e.g. assets.regus.com),
 * which the browser flags as mixed content on our https pages. Request them
 * over https directly.
 */
export const toHttps = (src: string) => src.replace(/^http:\/\//i, 'https://');

/**
 * IWG photos are 10–15 MB originals on Cloudinary, which has no /sm|/md|/lg
 * variants (those 404). Map the size suffix to a Cloudinary resize instead.
 * Returns null for any URL that isn't a suffixed IWG Cloudinary image.
 */
export function toIwgResizedUrl(src: string): string | null {
    const size = src.match(SIZE_SUFFIX_RE)?.[1]?.toLowerCase() as ImageSize | undefined;
    if (!size) return null;
    const parts = stripSizeSuffix(src).match(IWG_CLOUDINARY_RE);
    if (!parts) return null;
    // Already resized (a sized URL passed in again, e.g. by SafeImage):
    // a second transformation 404s, so keep the one it has.
    if (IWG_TRANSFORMED_RE.test(parts[2])) return `${parts[1]}${parts[2]}`;
    return `${parts[1]}w_${IWG_WIDTHS[size]},c_limit,q_auto,f_auto/${parts[2]}`;
}

/** The untransformed IWG original (5-7 MB) behind a sized or resized URL. */
export function toIwgOriginalUrl(src: string): string | null {
    const parts = stripSizeSuffix(src).match(IWG_CLOUDINARY_RE);
    if (!parts) return null;
    return `${parts[1]}${parts[2].replace(IWG_TRANSFORMED_RE, '')}`;
}

// Only our S3 buckets store /sm|/md|/lg|/xl variants. Other hosts (e.g.
// assets.regus.com) 404 on them, which costs a failed request before
// SafeImage falls back, so their URLs are returned unchanged.
const S3_VARIANTS_RE = /^https?:\/\/hotdesk(-staging)?-assets\.s3\.[^/]+\.amazonaws\.com\//i;

/** URL of `src` at a given size, for any of our image hosts. */
export function sizedImageUrl(src: string, size: ImageSize): string {
    src = toHttps(src);
    const suffixed = `${stripSizeSuffix(src)}/${size}`;
    return (
        toIwgResizedUrl(suffixed) ??
        (S3_VARIANTS_RE.test(src) ? suffixed : src)
    );
}
